import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { decryptToken } from '../Utlis/Encryption';
import { BASE_URL } from '../components/BaseUrl';
import { setUnreadCounts } from '../Redux/Slices/MessageCountSlice';
import socketService from '../services/socketService';

/**
 * Fetches global unread counts (API) and subscribes to socket total_unread_count_updated.
 * Updates Redux messageCount (totalCount, threadCounts, roleBased) so Sidebar and Inbox show correct badges.
 */
export function useGlobalUnreadCount() {
  const dispatch = useDispatch();
  const token = useSelector((state) => state.auth?.token);
  const decryptedToken = token ? decryptToken(token) : null;
  const refetchTimerRef = useRef(null);

  const fetchUnreadCounts = async () => {
    if (!decryptedToken) return;
    try {
      const res = await fetch(`${BASE_URL}/messages/unread-counts/detailed`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${decryptedToken}`,
          'Content-Type': 'application/json',
        },
      });
      const data = await res.json();
      if (data.error) return;
      const raw = data.data || data;
      const total = raw.total ?? raw.totalUnread;
      const threads = raw.threads ?? (Array.isArray(raw.threadCounts) ? raw.threadCounts : undefined);
      const roleBased = raw.roleBased;
      dispatch(setUnreadCounts({
        total: typeof total === 'number' ? total : undefined,
        threads,
        roleBased,
      }));
    } catch (err) {
      console.warn('useGlobalUnreadCount fetch failed:', err);
    }
  };

  useEffect(() => {
    if (!decryptedToken) return;

    fetchUnreadCounts();

    socketService.connect();
    const socket = socketService.getSocket();
    if (socket) {
      const onTotalUpdated = (data) => {
        const total = data?.totalUnread ?? data?.total_unread_count ?? data?.count;
        if (typeof total === 'number') {
          dispatch(setUnreadCounts({ total }));
        }
      };
      socket.on('total_unread_count_updated', onTotalUpdated);

      const scheduleRefetch = () => {
        if (refetchTimerRef.current) clearTimeout(refetchTimerRef.current);
        refetchTimerRef.current = setTimeout(fetchUnreadCounts, 500);
      };
      socket.on('unread_count_updated', scheduleRefetch);
      socket.on('new_message', scheduleRefetch);
      socket.on('messages_read', scheduleRefetch);

      // After we send a message, request updated total so sidebar count stays correct
      const onMessageSent = () => {
        socket.emit('get_total_unread_count');
      };
      socket.on('message_sent', onMessageSent);

      return () => {
        socket.off('total_unread_count_updated', onTotalUpdated);
        socket.off('unread_count_updated', scheduleRefetch);
        socket.off('new_message', scheduleRefetch);
        socket.off('messages_read', scheduleRefetch);
        socket.off('message_sent', onMessageSent);
        if (refetchTimerRef.current) clearTimeout(refetchTimerRef.current);
      };
    }
  }, [decryptedToken, dispatch]);

  useEffect(() => {
    if (!decryptedToken) return;
    const interval = setInterval(fetchUnreadCounts, 60000);
    return () => clearInterval(interval);
  }, [decryptedToken]);

  useEffect(() => {
    if (!decryptedToken) return;
    const onVisibility = () => {
      if (document.visibilityState === 'visible') fetchUnreadCounts();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [decryptedToken]);
}
